import styled from '@emotion/styled';
import { Chip as _Chip } from '@material-ui/core';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import { ClassAttributes, createElement, MouseEvent, useContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { MatchingFormat, SingleCreationForDetailFragment } from '../../../graphql/generated/graphql';
import theme from '../../../theme';
import ReputationDetail from '../../component/compound/ReputationDetail';
import Spinner from '../../component/compound/Spinner';
import _Breadcrumbs from '../../connected/Breadcrumbs';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { context as DependencyContext } from '../../context/dependency';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import CreationListItem from './CreationListItem';
import _Creations from './Creations';
import _CreativeBrief from './CreativeBrief';
import _OrganizationName from './OrganizationName';
import _ProjectTitle from './ProjectTitle';
import useGetCreationSetQuery from './useGetCreationSetQuery';
import useUpdateViewedAt from './useUpdateViewedAt';

type Props = ClassAttributes<HTMLElement> & {
  signedInUserId: string;
  offerId: string;
  assignmentId: string | null;
  onClickCreation: (e: MouseEvent<HTMLElement>, creationId: string) => void;
  onClickProfile: (url: string) => void;
  className?: string;
  profileWebUrl: string;
};

export const CreationSetDetailPage = ({
  offerId,
  assignmentId,
  onClickCreation,
  onClickProfile,
  className,
  profileWebUrl,
}: Props) => {
  const dependency = useContext(DependencyContext);
  const { creationSet, assignment, briefFiles, assetFiles, loading, reload } = useGetCreationSetQuery(
    offerId,
    assignmentId,
  );

  const project = creationSet?.project;

  const { updateViewedAt } = useUpdateViewedAt();
  useEffect(() => {
    if (assignment?.id) {
      updateViewedAt(assignment.id);
    }

    if (project?.id != null) {
      dependency.showProjectUsecase.prepare(project?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignment?.id, updateViewedAt, project?.id]);

  if (!creationSet || !assignment || !project) {
    return (
      <Root className={className}>
        <Spinner />
      </Root>
    );
  }
  const otherCreations =
    creationSet.creationsByOtherAssignedOptimizers?.filter((c): c is SingleCreationForDetailFragment => !!c) || [];

  const resizeBaseCreationList = creationSet.creativeFormats.filter((creativeFormat) => !creativeFormat.resizeBaseId);
  const creationListItems = resizeBaseCreationList.map((creativeFormat, _index) => {
    const creation = creationSet.creationsByAssignmentId.find((c) => c.creativeFormat.id == creativeFormat.id);
    return (
      <CreationListWithResize key={`creation_set_detail.creative_formats.${creativeFormat.id}`}>
        <CreationListItem
          creativeFormat={creativeFormat}
          creationSetId={creationSet.id}
          organization={project.organization}
          creation={creation}
          assignment={assignment}
          expressDueDays={typeof creationSet.expressDueDays == 'number' ? creationSet.expressDueDays : undefined}
        />
        {creationSet.creativeFormats.map((resizeCreativeFormat) => {
          const creation = creationSet.creationsByAssignmentId.find(
            (c) => c.creativeFormat.id == resizeCreativeFormat.id,
          );
          return creativeFormat.id === resizeCreativeFormat.resizeBaseId ? (
            <CreationListItem
              creativeFormat={resizeCreativeFormat}
              isResize={true}
              key={resizeCreativeFormat.id}
              creationSetId={creationSet.id}
              organization={project.organization}
              creation={creation}
              assignment={assignment}
              expressDueDays={typeof creationSet.expressDueDays == 'number' ? creationSet.expressDueDays : undefined}
            />
          ) : null;
        })}
      </CreationListWithResize>
    );
  });

  return (
    <Root className={className}>
      <Breadcrumbs />

      <ProjectTitle project={project} loading={loading} />

      {project.matchingFormat == MatchingFormat.Competition && (
        <CompetitionChip
          label={<FormattedMessage id="show_creation_page.label.competition" />}
          variant="outlined"
          color="primary"
          icon={<PeopleOutlineIcon />}
        />
      )}

      <OrganizationName organization={project.organization} loading={loading} />

      <Reputations>
        {loading ? <Spinner /> : null}
        {!loading && assignment.reputation && assignment.reputationToOffer ? (
          <ReputationDetail
            directorReputation={assignment.reputation}
            optimizerReputation={assignment.reputationToOffer}
            refresh={reload}
          />
        ) : null}
      </Reputations>

      <CreationSetList>{creationListItems}</CreationSetList>

      <Creations
        myCreations={creationSet.creationsByAssignmentId}
        otherCreations={otherCreations}
        loading={loading}
        project={project}
        onClickSome={onClickCreation}
        onClickProfile={onClickProfile}
        profileWebUrl={profileWebUrl}
      />

      <CreativeBrief
        creationSet={creationSet}
        hasProjectChange={project.hasChanges}
        assetFiles={assetFiles}
        briefFiles={briefFiles}
        loading={loading}
        reload={reload}
      />
    </Root>
  );
};

const Root = styled('div')({
  ...gridLayoutContainer({
    columns: ['auto', '1fr'],
    rows: ['auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
    areas: [
      ['breadcrumbs', 'breadcrumbs'],
      ['projectTitle', 'projectTitle'],
      ['competitionChip', 'organizationName'],
      ['reputations', 'reputations'],
      ['creation_set_list', 'creation_set_list'],
      ['creatives', 'creatives'],
      ['creativeBrief', 'creativeBrief'],
      ['relatedProjects', 'relatedProjects'],
    ],
  }),
  margin: `0 ${SPACING_UNIT}px`,
  padding: `${SPACING_UNIT * 5}px calc((100% - 960px) / 2) ${SPACING_UNIT * 10}px`,
});

const Breadcrumbs = styled(_Breadcrumbs)({ ...gridLayoutItem('breadcrumbs'), marginBottom: SPACING_UNIT * 5 });

const ProjectTitle = styled(_ProjectTitle)(gridLayoutItem('projectTitle'));

const OrganizationName = styled(_OrganizationName)({
  ...gridLayoutItem('organizationName'),
  marginBottom: SPACING_UNIT * 5,
});

const Creations = styled(_Creations)({ ...gridLayoutItem('creatives'), marginBottom: SPACING_UNIT * 5 });

const CreativeBrief = styled(_CreativeBrief)({ ...gridLayoutItem('creativeBrief') });

const CompetitionChip = styled(_Chip)(gridLayoutItem('competitionChip'), {
  marginRight: SPACING_UNIT * 3,
  minWidth: 100,
  maxWidth: 120,
  marginTop: '-3px',
});

const CreationListWithResize = styled('div')({
  marginBottom: SPACING_UNIT / 2,
  border: `1px solid ${theme.palette.divider}`,
});
const CreationSetList = styled('div')(gridLayoutItem('creation_set_list'), {
  padding: SPACING_REGULAR,
});

const Reputations = styled('div')(gridLayoutItem('reputations'), {
  padding: SPACING_REGULAR,
});
